// utils/imageProcessor.js

/**
 * Process image file before upload
 * - Resizes images to max width of 1000px (maintaining aspect ratio)
 * - Compresses images to max size of 500KB
 * - Converts HEIC images to JPEG
 * - Supports other common formats (PNG, JPEG, WebP, etc.)
 *
 * @param {File} file - Original file from file input or drop
 * @param {Object} options - Processing options
 * @param {number} options.maxSizeKB - Maximum file size in KB (default: 500)
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1000)
 * @param {string} options.outputFormat - Output format: 'jpeg' or 'png' (default: 'jpeg')
 * @param {number} options.quality - Image quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Processed file
 */
export async function processImage(
  file: File,
  options?: {
    maxSizeKB?: number;
    maxWidth?: number;
    outputFormat?: string;
    quality?: number;
  }
): Promise<File> {
  const { maxSizeKB = 500, maxWidth = 1000, outputFormat = 'jpeg', quality = 0.8 } = options || {};

  if (typeof window === 'undefined') {
    return file;
  }

  // Early return if not an image
  if (!file.type.startsWith('image/')) {
    return file;
  }

  try {
    // Handle HEIC images from iPhones
    let processedFile = file;
    if (file.type === 'image/heic' || file.type === 'image/heif') {
      const heicTo = (await import('heic-to')).heicTo;
      const blob = (await heicTo({
        blob: file,
        type: outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png',
        quality,
      })) as Blob;

      // Create a new file from the blob
      processedFile = new File(
        [blob],
        file.name.replace(/\.(heic|heif)$/i, outputFormat === 'jpeg' ? '.jpg' : '.png'),
        { type: outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png' }
      );
    }

    // Create image element to load the file
    const img = new Image();
    const imageUrl = URL.createObjectURL(processedFile);

    return new Promise((resolve, reject) => {
      img.onload = () => {
        URL.revokeObjectURL(imageUrl);

        // Calculate new dimensions (respecting aspect ratio)
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }

        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Failed to get canvas context');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob and compress
        const compress = async (currentQuality: number): Promise<File> => {
          const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) reject(new Error('Failed to create blob'));
                else resolve(blob);
              },
              outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png',
              currentQuality
            );
          });

          // If file is still too large and quality can be reduced further, compress more
          if (blob.size > maxSizeKB * 1024 && currentQuality > 0.1) {
            return compress(currentQuality - 0.1);
          }

          // Create final file
          const newFilename = file.name.replace(
            /\.[^/.]+$/,
            outputFormat === 'jpeg' ? '.jpg' : '.png'
          );
          const newFile = new File([blob], newFilename, {
            type: outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png',
          });

          return newFile;
        };

        // Start compression with initial quality
        compress(quality).then(resolve).catch(reject);
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = imageUrl;
    });
  } catch (error) {
    console.error('Image processing error:', error);
    return file; // Return original file if processing fails
  }
}
